/**
 * Where each player is, as far as the server lets this viewer see.
 *
 * Locations never ride the game snapshot: the server pushes each socket its own
 * filtered view on `PLAYER_LOCATIONS`, through the game type's location policy
 * (admins see every tracked player). That is why this is its own hook rather
 * than another field read off `selectedGame`.
 *
 * Each push is a COMPLETE snapshot, so state is replaced wholesale. One request on
 * mount and one per reconnect; never polled — the push channel carries every
 * change in between, and inbound events are rate-limited.
 *
 * Timestamps are corrected for clock skew against the snapshot's `emittedAt`, so
 * "2m ago" means two minutes by the SERVER's clock. An admin machine running a few
 * minutes fast would otherwise age every fix by that offset and make a live player
 * look stale.
 */
import { useEffect, useState } from 'react';
import { useSocketContext } from './SocketProvider';
import { SocketEvent, } from '@swtorbit/shared-socket-events';
const EMPTY = {};
export function usePlayerLocations(config) {
    const { enabled, roomCode } = config;
    const { socket } = useSocketContext();
    const [locations, setLocations] = useState(EMPTY);
    useEffect(() => {
        if (!socket || !enabled || !roomCode) {
            setLocations(EMPTY);
            return;
        }
        const apply = (payload) => {
            if (payload.roomCode !== roomCode)
                return;
            // Shift server timestamps into this machine's clock once, here, so every
            // consumer can compare them against a plain `Date.now()`.
            const skewMs = Date.now() - payload.emittedAt;
            const next = {};
            for (const loc of payload.players) {
                next[loc.playerId] = {
                    ...loc,
                    enteredAt: loc.enteredAt + skewMs,
                    positionAt: loc.positionAt + skewMs,
                };
            }
            setLocations(next);
        };
        const request = () => {
            const payload = { roomCode };
            socket.emit(SocketEvent.PLAYER_REQUEST_LOCATIONS, payload, (res) => {
                if (res?.success && res.state)
                    apply(res.state);
            });
        };
        socket.on(SocketEvent.PLAYER_LOCATIONS, apply);
        socket.on('connect', request);
        request();
        return () => {
            socket.off(SocketEvent.PLAYER_LOCATIONS, apply);
            socket.off('connect', request);
        };
    }, [socket, enabled, roomCode]);
    return locations;
}
